import { useEffect, useState } from 'react';
import { useWorkspaceFind } from './retrieval';
import type { WorkspaceFindHit, WorkspaceFindKind } from './types';

const SEMANTIC_DELAY_MS = 600;
const MAX_HITS = 200;

function appendMatches(keyword: WorkspaceFindHit[], semantic: WorkspaceFindHit[]) {
  const seen = new Set(keyword.map((hit) => `${hit.kind}:${hit.id}`));
  return [
    ...keyword,
    ...semantic.filter((hit) => {
      const id = `${hit.kind}:${hit.id}`;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    }),
  ].slice(0, MAX_HITS);
}

/** Keyword results never await a provider. Meaning matches append after typing stops;
 * both readers retain their own current owner/query generations and cancellation. */
export function useFinderSearch(
  open: boolean,
  query: string,
  kinds: WorkspaceFindKind[],
  owner: string,
) {
  const key = `${owner}\0${query.trim()}`;
  const [semanticKey, setSemanticKey] = useState('');
  useEffect(() => {
    setSemanticKey('');
    if (!open || !query.trim()) return;
    const timer = setTimeout(() => setSemanticKey(key), SEMANTIC_DELAY_MS);
    return () => clearTimeout(timer);
  }, [open, key]);
  const keyword = useWorkspaceFind(open, query, kinds, owner, undefined, 'keyword');
  const semantic = useWorkspaceFind(open && semanticKey === key, query, kinds, owner);
  const hits = keyword.loading ? keyword.hits : appendMatches(keyword.hits, semantic.hits);
  return {
    ...keyword,
    hits,
    // An optional enrichment error never removes the successful keyword answer.
    degraded: keyword.degraded || Boolean(semantic.error) || semantic.degraded,
    semanticStatus: semantic.semanticStatus ?? keyword.semanticStatus,
    hasMore: hits.length < MAX_HITS && (keyword.hasMore || semantic.hasMore),
    loadMore: keyword.hasMore ? keyword.loadMore : semantic.loadMore,
  };
}
